import React, { Component, type FC, type PropsWithChildren, useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';

import config from '../config';

import sentry from 'utils/sentry';
import * as storage from 'utils/storage';
import { getDecodedToken } from 'utils/token';
import { ACCESS_TOKEN, REFRESH_TOKEN } from 'constants/storage';
import type { DecodedToken, DecodedTokenData } from 'interface/token';

import * as mixpanel from 'services/mixPanel';
import { redirectToLogin } from 'services/auth';

import Router from './Router';
import Footer from 'components/footer';

import Toast from './common/toast';
import WorkPlanButtons from './common/work-plan-buttons';

sentry();

// Until the provider renders, Router still reads this default literal (matches previous JS behaviour).
export const UserContext = React.createContext<DecodedTokenData | 'user'>('user');

// Cookies are typed as primitives; jwt decode only accepts a string while we
// still branch on the raw value (same as the old class).
function tokenStringForDecode(raw: string | number | boolean | null | undefined): string | null {
  if (typeof raw === 'string') {
    return raw;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(raw);
  }
  return null;
}

function readUserFromTokens(): DecodedToken | null {
  try {
    const accessToken = storage.get(ACCESS_TOKEN);
    const decodedToken = getDecodedToken(tokenStringForDecode(accessToken));

    if (accessToken && decodedToken) {
      return decodedToken;
    }

    const refreshToken = storage.get(REFRESH_TOKEN);
    const decodedRefreshToken = getDecodedToken(tokenStringForDecode(refreshToken));

    if (refreshToken && decodedRefreshToken) {
      return decodedRefreshToken;
    }
  } catch {
    return null;
  }

  return null;
}

// Class boundary is the only way to keep the same Sentry reporting as the old App.componentDidCatch.
class AppErrorBoundary extends Component<PropsWithChildren> {
  componentDidCatch(error: Error) {
    Sentry.captureException(error);
  }

  render() {
    return this.props.children;
  }
}

const AppContent: FC = () => {
  const [user] = useState<DecodedTokenData | null>(() => readUserFromTokens()?.data ?? null);

  useEffect(() => {
    mixpanel.initialize(config.appName);

    if (!user) {
      redirectToLogin();
    }
  }, [user]);

  return user ? (
    <UserContext.Provider value={user}>
      <Router />
      <WorkPlanButtons />
      <Footer />
      <Toast />
    </UserContext.Provider>
  ) : null;
};

const App: FC = () => (
  <AppErrorBoundary>
    <AppContent />
  </AppErrorBoundary>
);

export default App;
